// What the schema pages share (spec/dashboard.md section 5): the Data structure tabs, the page environment, the write
// epilogue (the head moves, the catalog re-reads), the API's validation details as inline field errors, and the template
// actions of the list and the editor (clone, delete with the 409's counts).
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ApiError } from '../../api/client';
import { ops } from '../../api/operations';
import type { Job, Json, Parameter, Template } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { useContent } from '../../content/ContentContext';
import { heirsOf } from '../../content/widgets/context';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { useConfirm } from '../../ui/ConfirmDialog';
import { JobProgress } from '../../ui/JobProgress';
import { Modal } from '../../ui/Modal';
import { Tabs } from '../../ui/Tabs';
import { useToasts } from '../../ui/Toast';
import { useWorkspace } from '../../workspace/react';

export function SchemaTabs() {
  const { org, game } = useGame();
  const { branch = '' } = useParams();
  return (
    <Tabs
      ariaLabel="Data structure"
      items={[
        { key: 'templates', label: 'Templates', to: routes.templates(org, game.slug, branch) },
        { key: 'enums', label: 'Enums', to: routes.enums(org, game.slug, branch) },
        { key: 'profiles', label: 'Profiles', to: routes.profiles(org, game.slug, branch) },
        { key: 'tags', label: 'Tags', to: routes.tags(org, game.slug, branch) },
      ]}
    />
  );
}

export function useSchemaEnv() {
  const { org, game, can } = useGame();
  const { branch = '' } = useParams();
  const { client } = useAuth();
  const catalog = useContent();
  const workspace = useWorkspace();
  const toasts = useToasts();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const canWrite = can('content_write') && !workspace.isProtected;
  const path = { org, game: game.slug, branch };
  /** after a schema write: the branch head moves to the write's commit and every catalog query re-reads */
  const afterWrite = useCallback(
    (commit: string | null | undefined) => {
      if (commit) workspace.setHead(commit);
      catalog.refresh();
    },
    [workspace, catalog],
  );
  const fail = useCallback(
    (e: unknown, what: string) => {
      if (e instanceof ApiError) toasts.error(`${what}: ${e.message}`, { requestId: e.requestId });
      else toasts.error(`${what}: ${String(e)}`);
    },
    [toasts],
  );
  return { org, gameSlug: game.slug, gameId: game.id, branch, client, catalog, workspace, toasts, confirm, navigate, canWrite, path, afterWrite, fail, can };
}

/** The API's validation details by field: the full field path and its last segment (`parameters[0].name` → `name`). */
export function fieldErrors(e: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!(e instanceof ApiError) || !e.details) return out;
  for (const d of e.details) {
    if (!d.field) continue;
    out[d.field] ??= d.message;
    const last = d.field.split('.').pop()!.replace(/\[\d+\]$/, '');
    out[last] ??= d.message;
  }
  return out;
}

/** `documents=3; references=5` — the detail of a template delete refused for its documents. */
export function deleteCounts(e: ApiError): { documents: number; references: number } | null {
  const detail = e.details?.find((d) => d.field === 'confirmName');
  if (!detail) return null;
  const docs = /documents=(\d+)/.exec(detail.message);
  const refs = /references=(\d+)/.exec(detail.message);
  return { documents: docs ? Number(docs[1]) : 0, references: refs ? Number(refs[1]) : 0 };
}

/** A parameter as the create / add requests take it (the server assigns a new id). */
export function parameterRequest(p: Parameter, opts: { keepId?: boolean } = {}): Record<string, unknown> {
  return {
    id: opts.keepId ? p.id : null,
    name: p.name,
    displayName: p.displayName,
    description: p.description,
    type: p.type,
    of: p.of ? parameterRequest(p.of) : null,
    template: p.template,
    enum: p.enum,
    group: p.group,
    assetKinds: p.assetKinds ?? [],
    regex: p.regex,
    default: (p.default ?? null) as Json,
    isUnique: p.isUnique,
    isRequired: p.isRequired,
    usedInName: p.usedInName,
    flags: p.flags ?? [],
  };
}

/** The documents a parameter's removal touches: the template's own and every heir's (the catalog's counts). */
export function familyDocumentCount(catalog: ReturnType<typeof useContent>, template: Template): number {
  return Number(template.documentCount) + heirsOf(catalog, template.name).reduce((n, h) => n + Number(h.documentCount), 0);
}

/** `Hue.Item` → `Hue.ItemCopy`. */
export function copyName(name: string): string {
  return `${name}Copy`;
}

/**
 * Clone and delete, shared by the templates list and the template editor. The delete runs as a `template.delete` job
 * (`DELETE …?async=true` with `confirmName` and `If-Match`, spec/api-conventions.md section 21.5): `jobDialog` (the owner
 * renders it) follows the job and `remove` resolves when it finished.
 */
export function useTemplateActions(): { clone: (t: Template) => Promise<string | null>; remove: (t: Template) => Promise<boolean>; jobDialog: ReactNode } {
  const env = useSchemaEnv();
  const { client, path, toasts, confirm, navigate, afterWrite, fail, org, gameSlug, branch, canWrite } = env;
  const [deleting, setDeleting] = useState<{ template: Template; job: Job } | null>(null);
  const resolveRef = useRef<((ok: boolean) => void) | null>(null);
  const finishDelete = useCallback((ok: boolean) => {
    resolveRef.current?.(ok);
    resolveRef.current = null;
    setDeleting(null);
  }, []);

  const clone = useCallback(
    async (t: Template): Promise<string | null> => {
      try {
        const result = await client.call(ops.createTemplate, {
          path,
          body: {
            name: copyName(t.name),
            displayName: t.displayName ? `${t.displayName} Copy` : null,
            description: t.description,
            kind: t.kind,
            parent: t.parent,
            flags: t.flags,
            displayFormat: t.displayFormat,
            order: t.order.length > 0 ? t.order : null,
            parameters: t.parameters.map((p) => parameterRequest(p)) as never,
            message: `Clone template ${t.name}`,
          },
        });
        if (result.warning) toasts.warning(`Warning: ${result.warning}`);
        toasts.success(`Template ${result.data.name} created.`);
        afterWrite(result.commit);
        navigate(routes.template(org, gameSlug, branch, result.data.name));
        return result.data.name;
      } catch (e) {
        fail(e, `Clone of ${t.name} failed`);
        return null;
      }
    },
    [client, path, toasts, afterWrite, fail, navigate, org, gameSlug, branch],
  );

  const remove = useCallback(
    async (t: Template): Promise<boolean> => {
      const run = async (confirmName?: string): Promise<boolean> => {
        const result = await client.call(ops.deleteTemplate, { path: { ...path, template: t.name }, ifMatch: Number(t.version), query: { async: true, ...(confirmName ? { confirmName } : {}) } });
        if (result.status === 202) {
          // the job form: the dialog follows it; the removal lands (or fails) in the worker
          return new Promise<boolean>((resolve) => {
            resolveRef.current = resolve;
            setDeleting({ template: t, job: result.data as unknown as Job });
          });
        }
        afterWrite(result.commit ?? result.data?.commit);
        toasts.success(`Template ${t.name} deleted.`);
        return true;
      };
      try {
        if (Number(t.documentCount) === 0) {
          const ok = await confirm({ title: `Delete template ${t.displayName ?? t.name}?`, body: `${t.name} has no documents. The deletion is a commit of its own.`, confirmLabel: 'Delete template', tone: 'danger' });
          if (!ok) return false;
          return await run();
        }
        // the API answers 409 with the counts until the name is confirmed (R-CM-01)
        return await run();
      } catch (e) {
        const counts = e instanceof ApiError && e.status === 409 ? deleteCounts(e) : null;
        if (!counts) {
          fail(e, `Delete of ${t.name} failed`);
          return false;
        }
        const ok = await confirm({
          title: `Delete template ${t.displayName ?? t.name} and its documents?`,
          body: 'Every document of the template is deleted with it, in one commit.',
          impact: (
            <span>
              {counts.documents} document{counts.documents === 1 ? '' : 's'}, referenced {counts.references} time{counts.references === 1 ? '' : 's'}
            </span>
          ),
          typeToConfirm: t.name,
          confirmLabel: 'Delete template and documents',
          tone: 'danger',
        });
        if (!ok) return false;
        try {
          return await run(t.name);
        } catch (e2) {
          fail(e2, `Delete of ${t.name} failed`);
          return false;
        }
      }
    },
    [client, path, confirm, afterWrite, toasts, fail],
  );

  const onDeleteDone = useCallback(
    (job: Job) => {
      if (!deleting) return;
      const name = deleting.template.name;
      if (job.status === 'succeeded') {
        afterWrite((job.result as { commit?: string } | null)?.commit ?? null);
        toasts.success(`Template ${name} deleted.`);
        finishDelete(true);
      } else {
        const e = job.error as { message?: string } | null;
        toasts.error(`Delete of ${name} ${job.status}${e?.message ? `: ${e.message}` : ''}`);
      }
    },
    [deleting, afterWrite, toasts, finishDelete],
  );

  const jobDialog = deleting ? (
    <Modal title={`Deleting template ${deleting.template.displayName ?? deleting.template.name}`} open onClose={() => finishDelete(false)}>
      <div data-testid="template-delete-job">
        <JobProgress job={deleting.job} api={{ client, org, game: gameSlug, missingPermission: canWrite ? null : 'content_write' }} onDone={onDeleteDone} onRetry={(job) => setDeleting((d) => (d ? { ...d, job } : d))} />
      </div>
    </Modal>
  ) : null;

  return { clone, remove, jobDialog };
}

/** Yes / no as a dot with the word for assistive technology (colour never carries meaning alone). */
export function Dot({ on, label }: { on: boolean; label: string }) {
  return (
    <span className={`dot ${on ? 'dot-on' : 'dot-off'}`} role="img" aria-label={`${label}: ${on ? 'yes' : 'no'}`} title={`${label}: ${on ? 'yes' : 'no'}`} />
  );
}

/** Toggle chips over a flag catalogue (template flags, parameter flags), each with its one-line help. */
export function FlagChips({ catalog, value, onChange, disabled, label }: { catalog: { value: string; label: string; help: string }[]; value: string[]; onChange: (flags: string[]) => void; disabled?: boolean; label: string }) {
  return (
    <div className="flag-chips" role="group" aria-label={label}>
      {catalog.map((f) => {
        const on = value.includes(f.value);
        return (
          <button key={f.value} type="button" className={`chip flag-chip ${on ? 'chip-accent' : 'chip-outline'}`} aria-pressed={on} title={f.help} disabled={disabled} onClick={() => onChange(on ? value.filter((x) => x !== f.value) : [...value, f.value])}>
            {f.label}
          </button>
        );
      })}
      {value.length > 0 && (
        <ul className="flag-help small muted">
          {catalog
            .filter((f) => value.includes(f.value))
            .map((f) => (
              <li key={f.value}>
                <strong>{f.label}</strong>: {f.help}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
